import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MenuItem {
  id: string;
  menu_id: string;
  parent_id: string | null;
  label_en: string;
  label_ar: string;
  url: string;
  is_external: boolean;
  target: string;
  is_visible: boolean;
  sort_order: number;
  description_en: string | null;
  description_ar: string | null;
  group_key: string | null;
  children?: MenuItem[];
}

const FALLBACK_HEADER: MenuItem[] = [
  { id: "fh1", menu_id: "", parent_id: null, label_en: "Ecommerce", label_ar: "التجارة الإلكترونية", url: "/ecommerce", is_external: false, target: "_self", is_visible: true, sort_order: 1, description_en: null, description_ar: null, group_key: null },
  { id: "fh2", menu_id: "", parent_id: null, label_en: "Services", label_ar: "الخدمات", url: "/services", is_external: false, target: "_self", is_visible: true, sort_order: 2, description_en: null, description_ar: null, group_key: null },
  { id: "fh3", menu_id: "", parent_id: null, label_en: "Work", label_ar: "أعمالنا", url: "/work", is_external: false, target: "_self", is_visible: true, sort_order: 3, description_en: null, description_ar: null, group_key: null },
  { id: "fh4", menu_id: "", parent_id: null, label_en: "Blog", label_ar: "المدونة", url: "/blog", is_external: false, target: "_self", is_visible: true, sort_order: 4, description_en: null, description_ar: null, group_key: null },
];

const FALLBACK_FOOTER: MenuItem[] = [
  { id: "ff1", menu_id: "", parent_id: null, label_en: "About", label_ar: "", url: "/about", is_external: false, target: "_self", is_visible: true, sort_order: 1, description_en: null, description_ar: null, group_key: "company" },
  { id: "ff2", menu_id: "", parent_id: null, label_en: "Contact", label_ar: "", url: "/contact", is_external: false, target: "_self", is_visible: true, sort_order: 2, description_en: null, description_ar: null, group_key: "company" },
  { id: "ff3", menu_id: "", parent_id: null, label_en: "Work", label_ar: "", url: "/work", is_external: false, target: "_self", is_visible: true, sort_order: 3, description_en: null, description_ar: null, group_key: "company" },
  { id: "ff4", menu_id: "", parent_id: null, label_en: "Blog", label_ar: "", url: "/blog", is_external: false, target: "_self", is_visible: true, sort_order: 4, description_en: null, description_ar: null, group_key: "company" },
  { id: "ff5", menu_id: "", parent_id: null, label_en: "Shopify", label_ar: "", url: "/shopify", is_external: false, target: "_self", is_visible: true, sort_order: 1, description_en: null, description_ar: null, group_key: "solutions" },
  { id: "ff6", menu_id: "", parent_id: null, label_en: "WordPress", label_ar: "", url: "/wordpress", is_external: false, target: "_self", is_visible: true, sort_order: 2, description_en: null, description_ar: null, group_key: "solutions" },
  { id: "ff7", menu_id: "", parent_id: null, label_en: "Webflow", label_ar: "", url: "/webflow", is_external: false, target: "_self", is_visible: true, sort_order: 3, description_en: null, description_ar: null, group_key: "solutions" },
  { id: "ff8", menu_id: "", parent_id: null, label_en: "Privacy Policy", label_ar: "", url: "/privacy-policy", is_external: false, target: "_self", is_visible: true, sort_order: 1, description_en: null, description_ar: null, group_key: "legal" },
  { id: "ff9", menu_id: "", parent_id: null, label_en: "Terms of Service", label_ar: "", url: "/terms", is_external: false, target: "_self", is_visible: true, sort_order: 2, description_en: null, description_ar: null, group_key: "legal" },
];

const FALLBACK_BURGER: MenuItem[] = [
  { id: "fb1", menu_id: "", parent_id: null, label_en: "Portfolio", label_ar: "أعمالنا", url: "/work", is_external: false, target: "_self", is_visible: true, sort_order: 0, description_en: null, description_ar: null, group_key: "primary" },
  { id: "fb2", menu_id: "", parent_id: null, label_en: "Services", label_ar: "خدماتنا", url: "/services", is_external: false, target: "_self", is_visible: true, sort_order: 1, description_en: null, description_ar: null, group_key: "primary" },
  { id: "fb3", menu_id: "", parent_id: null, label_en: "Contact", label_ar: "تواصل معنا", url: "/contact", is_external: false, target: "_self", is_visible: true, sort_order: 2, description_en: null, description_ar: null, group_key: "primary" },
];

export function useMenuItems(menuKey: "header" | "footer" | "burger") {
  const fallback = menuKey === "header" ? FALLBACK_HEADER : menuKey === "footer" ? FALLBACK_FOOTER : FALLBACK_BURGER;
  const [items, setItems] = useState<MenuItem[]>(fallback);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchMenu = async () => {
      try {
        const { data: menu, error: menuError } = await supabase
          .from("menus")
          .select("id")
          .eq("key", menuKey)
          .single();

        if (cancelled) return;

        if (menuError || !menu) {
          setLoading(false);
          return;
        }

        const { data, error: itemsError } = await supabase
          .from("menu_items")
          .select("*")
          .eq("menu_id", menu.id)
          .eq("is_visible", true)
          .order("sort_order");

        if (cancelled) return;

        if (itemsError || !data || data.length === 0) {
          setLoading(false);
          return;
        }

        const all = (data as MenuItem[]) || [];

        if (menuKey === "burger") {
          // For burger, return all items flat — the Header component will group them
          setItems(all);
        } else {
          const roots = all.filter((i) => !i.parent_id);
          const children = all.filter((i) => i.parent_id);
          roots.forEach((r) => {
            r.children = children.filter((c) => c.parent_id === r.id).sort((a, b) => a.sort_order - b.sort_order);
          });
          setItems(roots);
        }
      } catch (err) {
        console.error(`[useMenuItems] Error for "${menuKey}":`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMenu();

    return () => { cancelled = true; };
  }, [menuKey]);

  return { items, loading };
}
